import React, { useState, useRef, useCallback } from "react";
import { getAuthHeaders } from "../utils/auth";
import { useLanguage } from "../utils/i18n";

const API = "/api";

export default function BookCreatorModal({ onClose, onCreated }) {
  const { t } = useLanguage();
  const [title, setTitle]   = useState("");
  const [text, setText]     = useState("");
  const [images, setImages] = useState([]); // [{dataUrl, base64}]
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState("");

  const addImageFromBlob = useCallback((blob) => {
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64  = dataUrl.split(",")[1];
      setImages((prev) => [...prev, { dataUrl, base64 }]);
    };
    reader.readAsDataURL(blob);
  }, []);

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        const blob = item.getAsFile();
        if (blob) addImageFromBlob(blob);
      }
    }
  }, [addImageFromBlob]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((f) =>
      f.type.startsWith("image/")
    );
    files.forEach(addImageFromBlob);
  }, [addImageFromBlob]);

  const handleFileInput = (e) => {
    const files = Array.from(e.target.files || []).filter((f) =>
      f.type.startsWith("image/")
    );
    files.forEach(addImageFromBlob);
    e.target.value = "";
  };

  const removeImage = (idx) => {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (!text.trim() && images.length === 0) {
      setError(t("createBookError"));
      return;
    }
    setLoading(true);
    setError("");
    try {
      const authHeaders = await getAuthHeaders();
      const res = await fetch(`${API}/html-books`, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          title: title.trim() || "My Book",
          text,
          images: images.map((i) => i.base64),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || "Failed to create book.");
        return;
      }
      onCreated(data);
    } catch {
      setError("Network error. Is the backend running?");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bcm-overlay" onClick={onClose}>
      <div className="book-creator-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bcm-header">
          <h2 className="bcm-title">{t("createBookModalTitle")}</h2>
          <button className="bcm-close" onClick={onClose} disabled={loading}>
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="bcm-body" onPaste={handlePaste}>
          <label className="bcm-label">{t("bookTitleLabel")}</label>
          <input
            type="text"
            className="bcm-input"
            placeholder={t("bookTitlePlaceholderBcm")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={loading}
          />

          <label className="bcm-label" style={{ marginTop: 18 }}>
            {t("bookContentTitle")} <span className="bcm-hint">{t("bookContentSubhint")}</span>
          </label>
          <textarea
            className="bcm-textarea"
            placeholder={t("bookContentPlaceholder")}
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={loading}
            rows={10}
          />

          <label className="bcm-label" style={{ marginTop: 18 }}>
            {t("bookImagesTitle")}{" "}
            <span className="bcm-hint">
              {t("bookImagesSubhint")}
            </span>
          </label>
          <div
            className="bcm-drop-zone"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
          >
            {images.length === 0 ? (
              <>
                <span className="bcm-drop-placeholder">
                  {t("bookDragDropPlaceholder")}
                </span>
                <label className="bcm-browse-btn">
                  {t("browseBtn")}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileInput}
                  />
                </label>
              </>
            ) : (
              <div className="bcm-thumbs">
                {images.map((img, idx) => (
                  <div key={idx} className="bcm-thumb-wrap">
                    <img
                      src={img.dataUrl}
                      alt={`img-${idx}`}
                      className="bcm-thumb"
                    />
                    <button
                      className="bcm-remove-img"
                      onClick={() => removeImage(idx)}
                    >
                      ✕
                    </button>
                  </div>
                ))}
                <label className="bcm-browse-btn">
                  {t("addMoreBtn")}
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    style={{ display: "none" }}
                    onChange={handleFileInput}
                  />
                </label>
              </div>
            )}
          </div>

          {error && <div className="bcm-error">{error}</div>}
        </div>

        {/* Footer */}
        <div className="bcm-footer">
          <button className="bcm-btn-cancel" onClick={onClose} disabled={loading}>
            {t("cancel")}
          </button>
          <button
            className="bcm-btn-create"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="bcm-spinner" /> {t("generatingLabel")}
              </>
            ) : (
              t("generateBookBtn")
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
